import {
  ArrowUpRight,
  type LucideProps
} from "lucide-react";
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  type HTMLAttributes,
  type ReactNode
} from "react";
import { setPointerGlow } from "./pointer";
import "./styles.css";

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export function AmbientBackground() {
  return (
    <>
      <div className="ambient-scene" aria-hidden="true">
        <div className="ambient-ribbon" />
        <div className="ambient-silk" />
      </div>
      <div className="ambient-grid" aria-hidden="true" />
    </>
  );
}

type VisualShellProps = HTMLAttributes<HTMLDivElement>;

export function VisualShell({ children, className, ...props }: VisualShellProps) {
  return (
    <>
      <AmbientBackground />
      <div className={cx("ui-page-shell", className)} {...props}>
        {children}
      </div>
    </>
  );
}

type GlassPanelProps = HTMLAttributes<HTMLDivElement>;

export function GlassPanel({ children, className, ...props }: GlassPanelProps) {
  return (
    <div className={cx("glass-panel", className)} {...props}>
      {children}
    </div>
  );
}

type GlassCardProps = HTMLAttributes<HTMLElement> & {
  href?: string;
  interactive?: boolean;
};

export function GlassCard({
  children,
  className,
  href,
  interactive = true,
  onPointerMove,
  ...props
}: GlassCardProps) {
  const classes = cx("glass-card", interactive && "glass-interactive", className);

  if (href) {
    return (
      <a
        className={classes}
        href={href}
        onPointerMove={onPointerMove ?? setPointerGlow}
        {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </a>
    );
  }

  return (
    <article
      className={classes}
      onPointerMove={interactive ? (onPointerMove ?? setPointerGlow) : onPointerMove}
      {...props}
    >
      {children}
    </article>
  );
}

type GlassButtonProps =
  | (AnchorHTMLAttributes<HTMLAnchorElement> & {
      href: string;
      type?: never;
    })
  | (ButtonHTMLAttributes<HTMLButtonElement> & {
      href?: never;
    });

export function GlassButton(props: GlassButtonProps) {
  if ("href" in props && props.href) {
    const { children, className, href, onPointerMove, ...anchorProps } = props;
    const classes = cx("glass-button", className);

    return (
      <a
        className={classes}
        href={href}
        onPointerMove={onPointerMove ?? setPointerGlow}
        {...anchorProps}
      >
        {children}
      </a>
    );
  }

  const {
    children,
    className,
    onPointerMove,
    ...buttonProps
  } = props as ButtonHTMLAttributes<HTMLButtonElement>;
  const classes = cx("glass-button", className);

  return (
    <button
      className={classes}
      type="button"
      onPointerMove={onPointerMove ?? setPointerGlow}
      {...buttonProps}
    >
      {children}
    </button>
  );
}

type ExternalLinkButtonProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  icon?: ComponentType<LucideProps>;
  children: ReactNode;
};

export function ExternalLinkButton({
  children,
  className,
  icon: Icon = ArrowUpRight,
  onPointerMove,
  ...props
}: ExternalLinkButtonProps) {
  return (
    <a
      className={cx("external-link-button", className)}
      onPointerMove={onPointerMove ?? setPointerGlow}
      {...props}
    >
      {children}
      <Icon aria-hidden="true" size={16} strokeWidth={2} />
    </a>
  );
}

type SectionHeaderProps = {
  label: string;
  title: string;
  description?: string;
  id?: string;
  className?: string;
};

export function SectionHeader({
  className,
  description,
  id,
  label,
  title
}: SectionHeaderProps) {
  return (
    <div className={cx("section-header", className)}>
      <span className="section-kicker">{label}</span>
      <h2 id={id}>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
  );
}

type MarkdownSectionProps = HTMLAttributes<HTMLElement> & {
  as?: "article" | "section";
};

export function MarkdownSection({
  as = "section",
  children,
  className,
  ...props
}: MarkdownSectionProps) {
  const Component = as;

  return (
    <Component className={cx("markdown-section", className)} {...props}>
      {children}
    </Component>
  );
}
